import Phaser from 'phaser';
import { Suit, GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';
import { createSymbolGlow, SymbolGlowResult, SUIT_COLORS } from '../utils/SymbolGlow';

interface SuitOrb {
  container: Phaser.GameObjects.Container;
  glow: SymbolGlowResult;
  suit: Suit;
  baseX: number;
  baseY: number;
  floatPhase: number;
  rotationPhase: number;
}

type SelectionCallback = (suit: Suit) => void;
type HoverCallback = (suit: Suit | null) => void;

/**
 * Mystical trump selection UI with floating, rotating suit orbs.
 * Features ambient particles, hover effects, and dramatic selection animations.
 */
export class TrumpSelector {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private suitOrbs: Map<Suit, SuitOrb> = new Map();
  private ambientEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private trailEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private titleText!: Phaser.GameObjects.Text;
  private titleGlow!: Phaser.GameObjects.Text;
  private farsiText!: Phaser.GameObjects.Text;
  private onSelectCallback: SelectionCallback | null = null;
  private onHoverCallback: HoverCallback | null = null;
  private isVisible: boolean = false;
  private isAnimatingSelection: boolean = false;
  private scoreboardPosition: { x: number; y: number } = { x: GAME_WIDTH - 200, y: 100 };

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.create();
  }

  /**
   * Set the target scoreboard position for the fly-to animation
   */
  setScoreboardPosition(x: number, y: number): void {
    this.scoreboardPosition = { x, y };
  }

  private create(): void {
    this.container = this.scene.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30);
    this.container.setDepth(10);
    this.container.setVisible(false);

    this.createTitle();
    this.createSuitOrbs();
    this.createAmbientParticles();
  }

  private createTitle(): void {
    // Glow layer for title
    this.titleGlow = this.scene.add
      .text(0, -90, 'Select Trump', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '24px',
        color: PERSIAN_THEME.hex.gold,
        stroke: PERSIAN_THEME.hex.gold,
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0.3);

    // Main title
    this.titleText = this.scene.add
      .text(0, -90, 'Select Trump', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '24px',
        color: PERSIAN_THEME.hex.gold,
        stroke: PERSIAN_THEME.hex.feltGreen,
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    // Farsi subtitle
    this.farsiText = this.scene.add
      .text(0, -65, PERSIAN_THEME.farsi.hokm, {
        fontFamily: PERSIAN_THEME.fonts.farsi,
        fontSize: '16px',
        color: PERSIAN_THEME.hex.bronze,
      })
      .setOrigin(0.5);

    this.container.add([this.titleGlow, this.titleText, this.farsiText]);
  }

  private createSuitOrbs(): void {
    // Diamond arrangement
    const positions: { suit: Suit; x: number; y: number }[] = [
      { suit: Suit.SPADES, x: 0, y: -25 }, // Top
      { suit: Suit.HEARTS, x: 65, y: 25 }, // Right
      { suit: Suit.DIAMONDS, x: 0, y: 75 }, // Bottom
      { suit: Suit.CLUBS, x: -65, y: 25 }, // Left
    ];

    positions.forEach(({ suit, x, y }, index) => {
      const orb = this.createSuitOrb(suit, x, y, index);
      this.suitOrbs.set(suit, orb);
    });
  }

  private createSuitOrb(suit: Suit, baseX: number, baseY: number, index: number): SuitOrb {
    const orbContainer = this.scene.add.container(baseX, baseY);
    const colors = SUIT_COLORS[suit];

    // Create symbol with glow using shared utility
    const glow = createSymbolGlow(this.scene, suit, {
      symbolSize: 60,
      mainColor: colors.main,
      glowColor: colors.glow,
      strokeThickness: 1.5,
      shadowOffset: 3,
    });

    orbContainer.add(glow.container);
    orbContainer.setSize(70, 70);
    orbContainer.setInteractive({ useHandCursor: true });

    // Hover effects
    orbContainer.on('pointerover', () => this.onOrbHover(suit, true));
    orbContainer.on('pointerout', () => this.onOrbHover(suit, false));
    orbContainer.on('pointerdown', () => this.onOrbSelect(suit));

    this.container.add(orbContainer);

    return {
      container: orbContainer,
      glow,
      suit,
      baseX,
      baseY,
      floatPhase: (index * Math.PI) / 2, // Offset each orb's float phase
      rotationPhase: index * 0.5,
    };
  }

  private createAmbientParticles(): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    this.ambientEmitter = this.scene.add.particles(0, 20, key, {
      frequency: 150,
      quantity: 1,
      lifespan: { min: 2000, max: 3500 },
      x: { min: -100, max: 100 },
      y: { min: -60, max: 80 },
      speedX: { min: -8, max: 8 },
      speedY: { min: -15, max: 5 },
      scale: { start: 0.25 / RENDER_SCALE, end: 0.05 / RENDER_SCALE },
      alpha: { start: 0.5, end: 0 },
      tint: [PERSIAN_THEME.colors.gold, 0xffd700, PERSIAN_THEME.colors.bronze],
      blendMode: Phaser.BlendModes.ADD,
    });

    this.ambientEmitter.stop();
    this.container.add(this.ambientEmitter);

    // Create trail emitter for fly-to-scoreboard animation
    this.trailEmitter = this.scene.add.particles(0, 0, key, {
      frequency: 30,
      quantity: 2,
      lifespan: { min: 300, max: 500 },
      speed: { min: 10, max: 30 },
      scale: { start: 0.15 / RENDER_SCALE, end: 0 },
      alpha: { start: 0.8, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
    });
    this.trailEmitter.stop();
    // Trail emitter is added to scene directly, not container, so it can follow world coordinates
  }

  private onOrbHover(suit: Suit, isHover: boolean): void {
    if (this.isAnimatingSelection) return;

    const orb = this.suitOrbs.get(suit);
    if (!orb) return;

    // Scale animation for hovered orb
    this.scene.tweens.add({
      targets: orb.container,
      scaleX: isHover ? 1.2 : 1,
      scaleY: isHover ? 1.2 : 1,
      duration: 150,
      ease: 'Back.easeOut',
    });

    // Glow intensity for hovered orb
    orb.glow.setGlowIntensity(isHover ? 1.8 : 1);
    if (orb.glow.shadow) {
      orb.glow.shadow.setAlpha(isHover ? 0.6 : 0.4);
    }

    // Mute/unmute non-hovered orbs
    this.suitOrbs.forEach((otherOrb, otherSuit) => {
      if (otherSuit !== suit) {
        const targetAlpha = isHover ? 0.4 : 1;
        this.scene.tweens.add({
          targets: otherOrb.container,
          alpha: targetAlpha,
          duration: 150,
        });
      }
    });

    // Particle burst on hover
    if (isHover) {
      this.createHoverBurst(orb);
    }

    // Emit hover callback for hand card highlighting
    if (this.onHoverCallback) {
      this.onHoverCallback(isHover ? suit : null);
    }
  }

  private createHoverBurst(orb: SuitOrb): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    const worldX = this.container.x + orb.container.x;
    const worldY = this.container.y + orb.container.y;

    // Spawn particles across the entire symbol area (60px symbol)
    const burst = this.scene.add.particles(worldX, worldY, key, {
      x: { min: -30, max: 30 },
      y: { min: -30, max: 30 },
      speed: { min: 50, max: 120 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.3 / RENDER_SCALE, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: 500,
      quantity: 24,
      tint: [PERSIAN_THEME.colors.gold, 0xffd700, 0xffec8b],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    });

    burst.setDepth(12);
    burst.explode();
    this.scene.time.delayedCall(700, () => burst.destroy());
  }

  private onOrbSelect(suit: Suit): void {
    if (this.isAnimatingSelection) return;
    this.isAnimatingSelection = true;

    // Disable all interactivity
    this.suitOrbs.forEach((orb) => {
      orb.container.disableInteractive();
    });

    // Animate selection
    this.animateSelection(suit);
  }

  private animateSelection(selectedSuit: Suit): void {
    const selectedOrb = this.suitOrbs.get(selectedSuit);
    if (!selectedOrb) return;

    // Golden burst on selected orb
    this.createSelectionBurst(selectedOrb);

    // Determine suit color for trail particles
    const isRed = selectedSuit === Suit.HEARTS || selectedSuit === Suit.DIAMONDS;
    const trailColor = isRed ? 0xff4444 : 0xffffff;

    // Animate selected orb - scale up with golden glow
    this.scene.tweens.add({
      targets: selectedOrb.container,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Calculate target position relative to container (container is at screen center)
        const containerX = this.container.x;
        const containerY = this.container.y;
        const targetRelX = this.scoreboardPosition.x - containerX;
        const targetRelY = this.scoreboardPosition.y - containerY;

        // Start particle trail
        if (this.trailEmitter) {
          this.trailEmitter.setParticleTint(trailColor);
          this.trailEmitter.setDepth(11);
          this.trailEmitter.start();
        }

        // Track position for trail particles
        const updateTrail = () => {
          if (this.trailEmitter && selectedOrb.container.active) {
            const worldX = containerX + selectedOrb.container.x;
            const worldY = containerY + selectedOrb.container.y;
            this.trailEmitter.setPosition(worldX, worldY);
          }
        };

        // Update trail position during the tween
        const trailTimer = this.scene.time.addEvent({
          delay: 16,
          repeat: -1,
          callback: updateTrail,
        });

        // Fly to scoreboard position
        this.scene.tweens.add({
          targets: selectedOrb.container,
          x: targetRelX,
          y: targetRelY,
          scaleX: 0.5,
          scaleY: 0.5,
          alpha: 0,
          duration: 500,
          ease: 'Power2.easeIn',
          onComplete: () => {
            // Stop trail
            trailTimer.destroy();
            if (this.trailEmitter) {
              this.trailEmitter.stop();
            }

            if (this.onSelectCallback) {
              this.onSelectCallback(selectedSuit);
            }
            this.hide();
          },
        });
      },
    });

    // Fly-away animations for non-selected suits
    const suits = [Suit.SPADES, Suit.HEARTS, Suit.DIAMONDS, Suit.CLUBS];
    const nonSelected = suits.filter((s) => s !== selectedSuit);

    nonSelected.forEach((suit, index) => {
      const orb = this.suitOrbs.get(suit);
      if (!orb) return;

      // Different fly-away animation for each
      switch (index) {
        case 0:
          this.animateSpiralOut(orb);
          break;
        case 1:
          this.animateFallDown(orb);
          break;
        case 2:
          this.animateExplodeUp(orb);
          break;
      }
    });

    // Fade out title
    this.scene.tweens.add({
      targets: [this.titleText, this.titleGlow, this.farsiText],
      alpha: 0,
      duration: 300,
    });
  }

  private createSelectionBurst(orb: SuitOrb): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    const worldX = this.container.x + orb.container.x;
    const worldY = this.container.y + orb.container.y;

    const burst = this.scene.add.particles(worldX, worldY, key, {
      speed: { min: 80, max: 200 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.4 / RENDER_SCALE, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 600,
      quantity: 30,
      tint: [PERSIAN_THEME.colors.gold, 0xffd700, 0xffec8b],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    });

    burst.setDepth(15);
    burst.explode();

    this.scene.time.delayedCall(1000, () => burst.destroy());
  }

  private animateSpiralOut(orb: SuitOrb): void {
    // Spiral outward clockwise with fade and particle trail
    const startAngle = Math.atan2(orb.baseY, orb.baseX);
    const radius = 200;

    // Create particle emitter for trail
    const key = 'gold-dust-particle';
    let trailEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
    if (this.scene.textures.exists(key)) {
      trailEmitter = this.scene.add.particles(0, 0, key, {
        frequency: 30,
        quantity: 2,
        lifespan: 300,
        scale: { start: 0.2 / RENDER_SCALE, end: 0 },
        alpha: { start: 0.6, end: 0 },
        tint: [PERSIAN_THEME.colors.gold, PERSIAN_THEME.colors.bronze],
        blendMode: Phaser.BlendModes.ADD,
        follow: orb.container,
        followOffset: { x: -this.container.x, y: -this.container.y },
      });
      trailEmitter.setDepth(11);
    }

    this.scene.tweens.add({
      targets: orb.container,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      duration: 600,
      ease: 'Power2.easeIn',
      onUpdate: (tween) => {
        const progress = tween.progress;
        const angle = startAngle + progress * Math.PI * 1.5;
        const dist = radius * progress;
        orb.container.x = orb.baseX + Math.cos(angle) * dist;
        orb.container.y = orb.baseY + Math.sin(angle) * dist;
        orb.container.rotation = progress * Math.PI * 2;
      },
      onComplete: () => {
        if (trailEmitter) {
          trailEmitter.stop();
          this.scene.time.delayedCall(400, () => trailEmitter?.destroy());
        }
      },
    });
  }

  private animateFallDown(orb: SuitOrb): void {
    // Fall downward with tumble and particle trail
    const key = 'gold-dust-particle';
    let trailEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
    if (this.scene.textures.exists(key)) {
      trailEmitter = this.scene.add.particles(0, 0, key, {
        frequency: 40,
        quantity: 1,
        lifespan: 400,
        scale: { start: 0.15 / RENDER_SCALE, end: 0 },
        alpha: { start: 0.5, end: 0 },
        speedY: { min: -20, max: -40 }, // Float upward as orb falls
        tint: [PERSIAN_THEME.colors.bronze, 0x8b7355],
        blendMode: Phaser.BlendModes.ADD,
        follow: orb.container,
        followOffset: { x: -this.container.x, y: -this.container.y },
      });
      trailEmitter.setDepth(11);
    }

    this.scene.tweens.add({
      targets: orb.container,
      y: orb.baseY + 300,
      alpha: 0,
      scaleX: 0.2,
      scaleY: 0.2,
      rotation: Math.PI * 2,
      duration: 700,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (trailEmitter) {
          trailEmitter.stop();
          this.scene.time.delayedCall(500, () => trailEmitter?.destroy());
        }
      },
    });
  }

  private animateExplodeUp(orb: SuitOrb): void {
    // Shoot upward and explode into particles
    this.scene.tweens.add({
      targets: orb.container,
      y: orb.baseY - 150,
      alpha: 0,
      scaleX: 0.1,
      scaleY: 0.1,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Particle explosion at final position
        const key = 'gold-dust-particle';
        if (!this.scene.textures.exists(key)) return;

        const worldX = this.container.x + orb.container.x;
        const worldY = this.container.y + orb.container.y;

        const burst = this.scene.add.particles(worldX, worldY, key, {
          speed: { min: 50, max: 150 },
          angle: { min: 200, max: 340 },
          scale: { start: 0.3 / RENDER_SCALE, end: 0 },
          alpha: { start: 0.8, end: 0 },
          lifespan: 500,
          quantity: 15,
          tint: [PERSIAN_THEME.colors.bronze, 0x8b7355],
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        });

        burst.setDepth(15);
        burst.explode();
        this.scene.time.delayedCall(800, () => burst.destroy());
      },
    });
  }

  /**
   * Update floating and rotation animations. Call in scene's update loop.
   */
  update(time: number): void {
    if (!this.isVisible || this.isAnimatingSelection) return;

    this.suitOrbs.forEach((orb) => {
      // Floating motion
      const floatOffset = Math.sin(time / 1000 + orb.floatPhase) * 5;
      orb.container.y = orb.baseY + floatOffset;

      // Subtle horizontal sway
      const swayOffset = Math.sin(time / 1500 + orb.floatPhase + 1) * 3;
      orb.container.x = orb.baseX + swayOffset;

      // Very slow rotation
      orb.container.rotation = Math.sin(time / 3000 + orb.rotationPhase) * 0.08;
    });

    // Title glow pulse
    const glowPulse = 0.25 + Math.sin(time / 800) * 0.1;
    this.titleGlow.setAlpha(glowPulse);
  }

  /**
   * Show the trump selector with entrance animation.
   */
  show(): void {
    if (this.isVisible) return;
    this.isVisible = true;
    this.isAnimatingSelection = false;

    // Reset all orbs
    this.suitOrbs.forEach((orb) => {
      orb.container.setScale(0);
      orb.container.setAlpha(1);
      orb.container.setRotation(0);
      orb.container.x = orb.baseX;
      orb.container.y = orb.baseY;
      orb.container.setInteractive({ useHandCursor: true });
      orb.glow.setGlowIntensity(1);
      if (orb.glow.shadow) {
        orb.glow.shadow.setAlpha(0.4);
      }
    });

    // Reset title
    this.titleText.setAlpha(0);
    this.titleGlow.setAlpha(0);
    this.farsiText.setAlpha(0);

    this.container.setVisible(true);

    // Entrance animation - fade in title
    this.scene.tweens.add({
      targets: [this.titleText, this.farsiText],
      alpha: 1,
      duration: 400,
      ease: 'Power2',
    });

    // Pop in each orb with stagger
    const suits = [Suit.SPADES, Suit.HEARTS, Suit.DIAMONDS, Suit.CLUBS];
    suits.forEach((suit, index) => {
      const orb = this.suitOrbs.get(suit);
      if (!orb) return;

      this.scene.tweens.add({
        targets: orb.container,
        scaleX: 1,
        scaleY: 1,
        duration: 400,
        delay: 100 + index * 80,
        ease: 'Back.easeOut',
      });
    });

    // Start ambient particles
    if (this.ambientEmitter) {
      this.ambientEmitter.start();
    }
  }

  /**
   * Hide the trump selector.
   */
  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;

    this.container.setVisible(false);

    // Stop ambient particles
    if (this.ambientEmitter) {
      this.ambientEmitter.stop();
    }

    // Clear hover state
    if (this.onHoverCallback) {
      this.onHoverCallback(null);
    }
  }

  /**
   * Set callback for when a suit is selected.
   */
  onSelect(callback: SelectionCallback): void {
    this.onSelectCallback = callback;
  }

  /**
   * Set callback for when hovering over a suit (for hand card highlighting).
   */
  onHover(callback: HoverCallback): void {
    this.onHoverCallback = callback;
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    this.ambientEmitter?.destroy();
    this.suitOrbs.forEach((orb) => orb.container.destroy());
    this.container.destroy();
  }
}
